import { useSyncExternalStore } from 'react';
import { getKstToday } from './date';

const DAY_MS = 24 * 60 * 60 * 1000;
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
/** 자정 직후 여유 — 타이머가 경계보다 살짝 일찍 깨어 아직 어제로 읽히는 것 방지. */
const MIDNIGHT_SLACK_MS = 1000;
/**
 * 재확인 상한 — 타이머를 자정까지 한 번에 걸지 않고 최대 이 간격으로 끊는다. 기기 절전 중엔 타이머 시계가 멈춰
 * (macOS 등) 긴 타이머가 잠든 시간만큼 밀리고, 화면을 띄운 채 깨어나면 복귀 이벤트도 안 온다 — 상한이 있으면
 * 그 경우에도 깨어난 뒤 이 간격 안에 넘어간다. 재확인 비용 = 날짜 문자열 1회 포맷·비교(같으면 알림 없음).
 */
const MAX_CHECK_INTERVAL_MS = 60 * 1000;

function msUntilNextCheck(): number {
  const untilMidnight = DAY_MS - ((Date.now() + KST_OFFSET_MS) % DAY_MS) + MIDNIGHT_SLACK_MS;
  return Math.min(untilMidnight, MAX_CHECK_INTERVAL_MS);
}

/**
 * 앱 전역 단일 날짜 소스 — 구독자가 몇이든 타이머 1개·리스너 1세트. 날짜가 바뀌면 **모든 구독자에게 한 번에**
 * 알려 React 가 한 배치로 리렌더한다(구독자별 타이머면 확인 시점이 갈려 한 화면에 오늘·어제가 섞인다).
 * 스냅샷도 캐시값(`current`) 하나를 공유해 어느 컴포넌트가 먼저 렌더돼도 같은 날짜를 읽는다.
 *   - 화면을 띄워둔 채 자정: 타이머가 자정 직후 깨운다.
 *   - 띄워둔 채 절전 후 깨어남: 재확인 상한으로 최대 1분 안에 넘어간다.
 *   - 백그라운드(모바일 WebView·비활성 탭) 복귀: 복귀 이벤트(visibilitychange·focus)가 즉시 재확인한다.
 */
const listeners = new Set<() => void>();
let current = getKstToday();
let timer: ReturnType<typeof setTimeout> | undefined;

function check(): void {
  const next = getKstToday();
  if (next === current) return;
  current = next;
  listeners.forEach((listener) => listener());
}

function schedule(): void {
  timer = setTimeout(() => {
    check();
    schedule();
  }, msUntilNextCheck());
}

function start(): void {
  check();
  schedule();
  document.addEventListener('visibilitychange', check);
  window.addEventListener('focus', check);
}

function stop(): void {
  clearTimeout(timer);
  timer = undefined;
  document.removeEventListener('visibilitychange', check);
  window.removeEventListener('focus', check);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stop();
  };
}

/** 구독자가 없는 동안엔 갱신이 멈춰 있으므로 첫 마운트 렌더에서 직접 맞춘다(구독 중엔 캐시값 공유). */
function getSnapshot(): string {
  if (listeners.size === 0) current = getKstToday();
  return current;
}

/**
 * KST '오늘'(`yyyy-MM-dd`)을 **리액티브**로 — 날짜가 바뀌면 리렌더를 일으킨다.
 *
 * `getKstToday()` 는 호출 시점 값일 뿐 리렌더 계기가 없어, 렌더 중 쿼리 키에 박으면 자정을 넘긴 SPA 세션이
 * 포커스 재조회·당겨서 새로고침 때 **어제 키**를 그대로 재조회한다. 쿼리 키·메모 deps 로 들어가는 '오늘'은
 * 이 훅으로 읽는다(렌더 밖 — decode·세터·클램프 — 는 `getKstToday()` 그대로). 규칙: docs/adr/kst-today.md
 */
export function useKstToday(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
